<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css/bundle';
import { Autoplay, Pagination } from 'swiper/modules';
import type SiteTopBanner from '@store/content/entities/SiteTopBanner';

const { items } = defineProps<{
    items: SiteTopBanner[];
}>();
const contentStore = useContentStore();
const { homepageMessagesLoading } = classToRefs(contentStore);
const pagination = ref<HTMLDivElement>();
const paginationOtions = computed(() => ({
    el: pagination.value,
    clickable: true,
}));

function openUrl(item: SiteTopBanner) {
    if (item?.link) {
        window.open(item.link, '_blank');
    }
}
</script>

<template>
    <div class="banner-carousel-container">
        <ClientOnly>
            <Swiper
                :slides-per-view="1"
                :free-mode="false"
                :autoplay="{
                    delay: 10000,
                }"
                :space-between="0"
                :breakpoints="{
                    820: {
                        spaceBetween: 10,
                        slidesPerView: 'auto',
                        freeMode: true,
                    },
                }"
                :pagination="paginationOtions"
                :modules="[Autoplay, Pagination]"
            >
                <SwiperSlide
                    v-for="(item, index) in items"
                    :id="`slider-click-${item.id}`"
                    :key="item.id ?? `banner-${index}`"
                    class="swiper-slide"
                    :class="{
                        'loading-skeleton': homepageMessagesLoading,
                        clickable: item.link,
                    }"
                    :aria-label="
                        item.desktopImage?.alt ??
                        item.mobileImage?.alt ??
                        'لینک بنر'
                    "
                    @click="openUrl(item)"
                >
                    <mr-blur-hash-image
                        v-if="item.desktopImage"
                        class="desktop-only"
                        :src="item.desktopImage.src"
                        :hash="item.desktopImage.blurHash"
                        :width="592"
                        :height="270"
                        hide-blurhash-onload
                        :alt="item.desktopImage.alt ?? 'بنر سایت مِستربلیط'"
                    />
                    <mr-blur-hash-image
                        v-if="item.mobileImage"
                        class="mobile-only"
                        :src="item.mobileImage.src"
                        hash="|lENV-EExaf3NgR;oHR.WrcIMxbJs+s*WBs+WEs,E2xWW=WCoea~j@WXoLsRjss+R.WYoLbIaxWEkXNgoIjYn#oIj]f5jY$ws7ofR.W?WrWBbIbINMS6WYoHoIj@jsayodj[ocWCj]azayj[ayayoHoIj@a$WYbHayj[WX"
                        hide-blurhash-onload
                        :alt="item.mobileImage.alt ?? 'بنر سایت مِستربلیط'"
                        :blurhash-config="{ ratio: 2.222222222, size: 125 }"
                    />
                </SwiperSlide>
            </Swiper>
        </ClientOnly>
        <div ref="pagination" class="custom-pagination" />
    </div>
</template>

<style lang="scss" scoped>
.swiper {
    width: 100%;
    height: 100%;
}

.clickable {
    cursor: pointer;
}
.banner-carousel-container {
    position: relative;
    width: 100%;
    overflow-x: hidden;
    &.loading {
        overflow-y: hidden;
    }
}

.swiper-slide {
    width: 595px;
    height: 275px;
    > * {
        width: 592px;
        height: 270px;
    }
    &.loading-skeleton {
        pointer-events: none;
    }
    :deep(img) {
        object-fit: cover;
        border-radius: 12px;
    }
    :deep(canvas) {
        border-radius: 12px;
    }
}

.custom-pagination {
    position: relative !important;
    bottom: 0 !important;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 8px;
}

@media (max-width: $app-mobile-max-width) {
    .swiper-slide {
        position: relative;
        width: 100%;
        height: auto;
        padding-top: 54.87%;
        > * {
            position: absolute !important;
            top: 0 !important;
            width: 100% !important;
            height: 100% !important;
        }
        :deep(img) {
            object-fit: cover;
            border-radius: 12px;
        }
    }
}
</style>
